import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../../components/feedback/ToastProvider";
import {
  AlertCircle,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Database,
  ExternalLink,
  History,
  Link2,
  Plus,
  RefreshCw,
  Save,
  TrendingUp,
  X,
} from "lucide-react";
import { createAdminAbexIndex, getAdminAbexIndex } from "./abexApi";
import "./AdminReferenceDataPage.css";

const DEFAULT_SOURCE_URL = "https://abex.be/nl/abex-index/";

function parseDate(dateString) {
  if (!dateString) {
    return null;
  }

  const parts = dateString.split("-").map(Number);

  if (parts.length !== 3) {
    return null;
  }

  const [year, month, day] = parts;

  return new Date(year, month - 1, day, 12, 0, 0);
}

function getToday() {
  const today = new Date();

  return new Date(
    today.getFullYear(),

    today.getMonth(),

    today.getDate(),

    12,

    0,

    0,
  );
}

function compareDateStrings(a, b) {
  const dateA = parseDate(a);

  const dateB = parseDate(b);

  if (!dateA && !dateB) {
    return 0;
  }

  if (!dateA) {
    return 1;
  }

  if (!dateB) {
    return -1;
  }

  return dateA.getTime() - dateB.getTime();
}

function getNextEffectiveDate(history = []) {
  if (history.length) {
    const sorted = [...history].sort((a, b) =>
      compareDateStrings(b.effective_date, a.effective_date),
    );

    const latest = parseDate(sorted[0]?.effective_date);

    if (latest) {
      const year = latest.getFullYear();

      const month = latest.getMonth();

      if (month === 0) {
        return `${year}-07-01`;
      }

      if (month === 6) {
        return `${year + 1}-01-01`;
      }
    }
  }

  const today = getToday();

  const year = today.getFullYear();

  const july = new Date(year, 6, 1, 12, 0, 0);

  if (today < july) {
    return `${year}-07-01`;
  }

  return `${year + 1}-01-01`;
}

function normaliseFieldError(value) {
  if (!value) {
    return "";
  }

  if (Array.isArray(value)) {
    return value.join(" ");
  }

  if (typeof value === "string") {
    return value;
  }

  return "";
}

export default function AdminReferenceDataPage() {
  const { t, i18n } = useTranslation("admin-reference-data");

  const { getAccessTokenSilently } = useAuth0();
  const { showToast } = useToast();

  const [data, setData] = React.useState(null);

  const [loading, setLoading] = React.useState(true);

  const [refreshing, setRefreshing] = React.useState(false);

  const [pageError, setPageError] = React.useState("");

  const [modalOpen, setModalOpen] = React.useState(false);

  const [saving, setSaving] = React.useState(false);

  const [fieldErrors, setFieldErrors] = React.useState({});

  const [form, setForm] = React.useState({
    effective_date: "",

    value: "",

    source_url: DEFAULT_SOURCE_URL,
  });

  const locale = i18n.resolvedLanguage || i18n.language || "en";

  const formatDate = React.useCallback(
    (dateString) => {
      const date = parseDate(dateString);

      if (!date) {
        return "—";
      }

      return new Intl.DateTimeFormat(locale, {
        day: "2-digit",

        month: "short",

        year: "numeric",
      }).format(date);
    },

    [locale],
  );

  const loadData = React.useCallback(
    async ({ initial = false } = {}) => {
      if (initial) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      setPageError("");

      try {
        const token = await getAccessTokenSilently();

        const response = await getAdminAbexIndex(token);

        setData(response);
      } catch (error) {
        setPageError(error?.message || t("errors.load"));
      } finally {
        setLoading(false);

        setRefreshing(false);
      }
    },

    [getAccessTokenSilently, showToast, t],
  );

  React.useEffect(() => {
    loadData({
      initial: true,
    });
  }, [loadData]);
  React.useEffect(() => {
    if (!modalOpen) {
      return undefined;
    }

    const originalOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !saving) {
        setModalOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;

      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [modalOpen, saving]);

  const history = React.useMemo(() => {
    if (!Array.isArray(data?.history)) {
      return [];
    }

    return [...data.history].sort((a, b) =>
      compareDateStrings(b.effective_date, a.effective_date),
    );
  }, [data]);

  const current = React.useMemo(() => {
    const today = getToday();

    const returnedCurrent = data?.current;

    if (returnedCurrent?.effective_date) {
      const currentDate = parseDate(returnedCurrent.effective_date);

      if (currentDate && currentDate <= today) {
        return returnedCurrent;
      }
    }

    return (
      history.find((item) => {
        const date = parseDate(item.effective_date);

        return date && date <= today;
      }) || null
    );
  }, [data?.current, history]);

  const nextScheduled = React.useMemo(() => {
    const today = getToday();

    if (data?.next?.effective_date) {
      const nextDate = parseDate(data.next.effective_date);

      if (nextDate && nextDate > today) {
        return data.next;
      }
    }

    const futureItems = history

      .filter((item) => {
        const date = parseDate(item.effective_date);

        return date && date > today;
      })

      .sort((a, b) => compareDateStrings(a.effective_date, b.effective_date));

    return futureItems[0] || null;
  }, [data?.next, history]);

  const getItemStatus = React.useCallback(
    (item) => {
      const itemDate = parseDate(item.effective_date);

      const today = getToday();

      if (!itemDate) {
        return "historical";
      }

      if (current?.id === item.id) {
        return "current";
      }

      if (itemDate > today) {
        return "scheduled";
      }

      return "historical";
    },

    [current],
  );

  const openAddModal = () => {
    setFieldErrors({});

    setForm({
      effective_date: getNextEffectiveDate(history),

      value: "",

      source_url: DEFAULT_SOURCE_URL,
    });

    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) {
      return;
    }

    setModalOpen(false);

    setFieldErrors({});
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,

      [name]: value,
    }));

    setFieldErrors((previous) => ({
      ...previous,

      [name]: "",

      general: "",
    }));
  };

  const validateForm = () => {
    const errors = {};

    if (!form.effective_date) {
      errors.effective_date = t("validation.effectiveDateRequired");
    } else {
      const date = parseDate(form.effective_date);

      if (!date || date.getDate() !== 1 || ![0, 6].includes(date.getMonth())) {
        errors.effective_date = t("validation.effectiveDateCycle");
      }
    }

    if (form.value === "" || form.value === null) {
      errors.value = t("validation.valueRequired");
    } else {
      const numericValue = Number(form.value);

      if (Number.isNaN(numericValue) || numericValue <= 0) {
        errors.value = t("validation.valuePositive");
      }
    }

    if (form.source_url && !/^https?:\/\//i.test(form.source_url)) {
      errors.source_url = t("validation.sourceUrl");
    }

    setFieldErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);

    setFieldErrors({});

    try {
      const token = await getAccessTokenSilently();

      await createAdminAbexIndex(token, {
        effective_date: form.effective_date,

        value: String(form.value).trim(),

        source_url: form.source_url.trim() || DEFAULT_SOURCE_URL,
      });

      setModalOpen(false);

      showToast({
        type: "success",
        title: t("abex.title"),
        message: t("success.created"),
      });

      await loadData();
    } catch (error) {
      const backendErrors = error?.data;

      if (backendErrors && typeof backendErrors === "object") {
        setFieldErrors({
          effective_date: normaliseFieldError(backendErrors.effective_date),

          value: normaliseFieldError(backendErrors.value),

          source_url: normaliseFieldError(backendErrors.source_url),

          general:
            normaliseFieldError(backendErrors.detail) ||
            normaliseFieldError(backendErrors.non_field_errors) ||
            error?.message ||
            t("errors.create"),
        });
      } else {
        setFieldErrors({
          general: error?.message || t("errors.create"),
        });
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <main className="admin-reference">
        <div className="admin-reference__loading">
          <div className="admin-reference__loading-header">
            <div className="admin-reference__skeleton admin-reference__skeleton--eyebrow" />

            <div className="admin-reference__skeleton admin-reference__skeleton--title" />

            <div className="admin-reference__skeleton admin-reference__skeleton--description" />
          </div>

          <div className="admin-reference__skeleton admin-reference__skeleton--large-card" />

          <div className="admin-reference__skeleton admin-reference__skeleton--table" />
        </div>
      </main>
    );
  }

  return (
    <main className="admin-reference">
      <div className="admin-reference__inner">
        <header className="admin-reference__page-header">
          <div className="admin-reference__page-heading">
            <span className="admin-reference__eyebrow">
              <Database size={16} strokeWidth={2} />

              {t("page.eyebrow")}
            </span>

            <h1>{t("page.title")}</h1>

            <p>{t("page.description")}</p>
          </div>

          <div className="admin-reference__header-actions">
            <button
              type="button"
              className="admin-reference__button admin-reference__button--secondary"
              onClick={() => loadData()}
              disabled={refreshing}
            >
              <RefreshCw
                size={18}
                className={refreshing ? "is-spinning" : ""}
              />

              <span>{t("actions.refresh")}</span>
            </button>

            <button
              type="button"
              className="admin-reference__button admin-reference__button--primary"
              onClick={openAddModal}
            >
              <Plus size={19} />

              <span>{t("actions.addValue")}</span>
            </button>
          </div>
        </header>

        {pageError && (
          <div
            className="admin-reference__alert admin-reference__alert--error"
            role="alert"
          >
            <AlertCircle size={20} />

            <div>
              <strong>{t("errors.loadTitle")}</strong>

              <span>{pageError}</span>
            </div>

            <button type="button" onClick={() => loadData()}>
              {t("actions.tryAgain")}
            </button>
          </div>
        )}

        <section className="admin-reference__abex-card">
          <div className="admin-reference__abex-header">
            <div className="admin-reference__abex-icon">
              <TrendingUp size={26} />
            </div>

            <div className="admin-reference__abex-heading">
              <div className="admin-reference__title-line">
                <h2>{t("abex.title")}</h2>

                <span className="admin-reference__region-badge">
                  {t("abex.region")}
                </span>
              </div>

              <p>{t("abex.description")}</p>
            </div>
          </div>

          <div className="admin-reference__summary-grid">
            <article className="admin-reference__summary-card admin-reference__summary-card--current">
              <div className="admin-reference__summary-top">
                <span className="admin-reference__summary-icon admin-reference__summary-icon--current">
                  <CheckCircle2 size={20} />
                </span>

                <span className="admin-reference__summary-label">
                  {t("abex.current.label")}
                </span>
              </div>

              <div className="admin-reference__value">
                {current?.value ?? "—"}
              </div>

              <div className="admin-reference__summary-meta">
                <CalendarDays size={16} />

                {current ? (
                  <span>
                    {t("abex.current.effectiveFrom", {
                      date: formatDate(current.effective_date),
                    })}
                  </span>
                ) : (
                  <span>{t("abex.current.empty")}</span>
                )}
              </div>

              {current?.source_url && (
                <a
                  href={current.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="admin-reference__source-link"
                >
                  <Link2 size={15} />

                  {t("abex.source")}

                  <ExternalLink size={14} />
                </a>
              )}
            </article>

            <article className="admin-reference__summary-card admin-reference__summary-card--scheduled">
              <div className="admin-reference__summary-top">
                <span className="admin-reference__summary-icon admin-reference__summary-icon--scheduled">
                  <Clock3 size={20} />
                </span>

                <span className="admin-reference__summary-label">
                  {t("abex.next.label")}
                </span>
              </div>

              <div className="admin-reference__value">
                {nextScheduled?.value ?? "—"}
              </div>

              <div className="admin-reference__summary-meta">
                <CalendarDays size={16} />

                {nextScheduled ? (
                  <span>
                    {t("abex.next.effectiveFrom", {
                      date: formatDate(nextScheduled.effective_date),
                    })}
                  </span>
                ) : (
                  <span>{t("abex.next.empty")}</span>
                )}
              </div>
            </article>
          </div>
        </section>

        <section className="admin-reference__history-card">
          <div className="admin-reference__history-header">
            <div>
              <div className="admin-reference__section-title">
                <History size={21} />

                <h2>{t("history.title")}</h2>
              </div>

              <p>{t("history.description")}</p>
            </div>

            <span className="admin-reference__count">
              {t("history.count", {
                count: history.length,
              })}
            </span>
          </div>

          {history.length === 0 ? (
            <div className="admin-reference__empty">
              <div className="admin-reference__empty-icon">
                <Database size={28} />
              </div>

              <h3>{t("history.emptyTitle")}</h3>

              <p>{t("history.emptyDescription")}</p>

              <button
                type="button"
                className="admin-reference__button admin-reference__button--primary"
                onClick={openAddModal}
              >
                <Plus size={18} />

                {t("actions.addValue")}
              </button>
            </div>
          ) : (
            <div className="admin-reference__table-wrap">
              <table className="admin-reference__table">
                <thead>
                  <tr>
                    <th>{t("history.columns.date")}</th>

                    <th>{t("history.columns.value")}</th>

                    <th>{t("history.columns.status")}</th>

                    <th>{t("history.columns.source")}</th>
                  </tr>
                </thead>

                <tbody>
                  {history.map((item) => {
                    const itemStatus = getItemStatus(item);

                    return (
                      <tr key={item.id}>
                        <td data-label={t("history.columns.date")}>
                          <div className="admin-reference__date-cell">
                            <CalendarDays size={16} />

                            {formatDate(item.effective_date)}
                          </div>
                        </td>

                        <td data-label={t("history.columns.value")}>
                          <strong className="admin-reference__table-value">
                            {item.value}
                          </strong>
                        </td>

                        <td data-label={t("history.columns.status")}>
                          <span
                            className={`admin-reference__status admin-reference__status--${itemStatus}`}
                          >
                            <span />

                            {t(`history.status.${itemStatus}`)}
                          </span>
                        </td>

                        <td data-label={t("history.columns.source")}>
                          {item.source_url ? (
                            <a
                              href={item.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="admin-reference__table-link"
                            >
                              {t("history.sourceLink")}

                              <ExternalLink size={14} />
                            </a>
                          ) : (
                            <span className="admin-reference__muted">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
      {modalOpen && (
        <div
          className="admin-reference-modal__overlay"
          onMouseDown={closeModal}
        >
          <div
            className="admin-reference-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-abex-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="admin-reference-modal__header">
              <div>
                <span className="admin-reference-modal__eyebrow">
                  <TrendingUp size={15} />

                  {t("modal.eyebrow")}
                </span>

                <h2 id="add-abex-title">{t("modal.title")}</h2>

                <p>{t("modal.description")}</p>
              </div>

              <button
                type="button"
                className="admin-reference-modal__close"
                onClick={closeModal}
                disabled={saving}
                aria-label={t("actions.close")}
              >
                <X size={21} />
              </button>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              <div className="admin-reference-modal__body">
                {fieldErrors.general && (
                  <div className="admin-reference-modal__error" role="alert">
                    <AlertCircle size={19} />

                    <span>{fieldErrors.general}</span>
                  </div>
                )}

                <div className="admin-reference-modal__field">
                  <label htmlFor="abex-effective-date">
                    {t("modal.fields.effectiveDate.label")}

                    <span>*</span>
                  </label>

                  <div className="admin-reference-modal__input-wrap">
                    <CalendarDays size={18} />

                    <input
                      id="abex-effective-date"
                      type="date"
                      name="effective_date"
                      value={form.effective_date}
                      onChange={handleFormChange}
                      className={fieldErrors.effective_date ? "has-error" : ""}
                      disabled={saving}
                    />
                  </div>

                  {fieldErrors.effective_date ? (
                    <span className="admin-reference-modal__field-error">
                      {fieldErrors.effective_date}
                    </span>
                  ) : (
                    <span className="admin-reference-modal__helper">
                      {t("modal.fields.effectiveDate.helper")}
                    </span>
                  )}
                </div>

                <div className="admin-reference-modal__field">
                  <label htmlFor="abex-value">
                    {t("modal.fields.value.label")}

                    <span>*</span>
                  </label>

                  <div className="admin-reference-modal__input-wrap">
                    <TrendingUp size={18} />

                    <input
                      id="abex-value"
                      type="number"
                      name="value"
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                      value={form.value}
                      onChange={handleFormChange}
                      placeholder={t("modal.fields.value.placeholder")}
                      className={fieldErrors.value ? "has-error" : ""}
                      disabled={saving}
                    />
                  </div>

                  {fieldErrors.value && (
                    <span className="admin-reference-modal__field-error">
                      {fieldErrors.value}
                    </span>
                  )}
                </div>

                <div className="admin-reference-modal__field">
                  <label htmlFor="abex-source-url">
                    {t("modal.fields.source.label")}
                  </label>

                  <div className="admin-reference-modal__input-wrap">
                    <Link2 size={18} />

                    <input
                      id="abex-source-url"
                      type="url"
                      name="source_url"
                      value={form.source_url}
                      onChange={handleFormChange}
                      placeholder="https://..."
                      className={fieldErrors.source_url ? "has-error" : ""}
                      disabled={saving}
                    />
                  </div>

                  {fieldErrors.source_url ? (
                    <span className="admin-reference-modal__field-error">
                      {fieldErrors.source_url}
                    </span>
                  ) : (
                    <span className="admin-reference-modal__helper">
                      {t("modal.fields.source.helper")}
                    </span>
                  )}
                </div>

                <div className="admin-reference-modal__info">
                  <AlertCircle size={18} />

                  <p>{t("modal.info")}</p>
                </div>
              </div>

              <div className="admin-reference-modal__footer">
                <button
                  type="button"
                  className="admin-reference__button admin-reference__button--secondary"
                  onClick={closeModal}
                  disabled={saving}
                >
                  {t("actions.cancel")}
                </button>

                <button
                  type="submit"
                  className="admin-reference__button admin-reference__button--primary"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <RefreshCw size={18} className="is-spinning" />

                      {t("actions.saving")}
                    </>
                  ) : (
                    <>
                      <Save size={18} />

                      {t("actions.save")}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
}
